import { WorkflowActionId, WorkflowExecutionId, WorkflowId } from "@sp-crm/core";
import { WorkflowExecutionResult } from "generated/graphql";

export interface WorkflowRunSequenceWorkflow {
    id: WorkflowId;
    actions: ReadonlyArray<{ id: WorkflowActionId | null }>;
}

export interface WorkflowRunSequenceExecution {
    id: WorkflowExecutionId;
    actionId: WorkflowActionId | null;
    dateAdded: string;
    scheduledAt: string | null;
    cancelledAt: string | null;
    finishedAt: string | null;
    result: WorkflowExecutionResult | null;
    workflow: WorkflowRunSequenceWorkflow;
}

export type WorkflowRunSequenceState =
    | "inProgress"
    | "completed"
    | "completedWithWarnings"
    | "stopped"
    | "cancelled";

export type WorkflowRunSequenceRow<T> =
    | { kind: "executed"; key: string; position: number | null; execution: T }
    | { kind: "notRun"; key: string; position: number };

export interface WorkflowRunSequence<T extends WorkflowRunSequenceExecution> {
    id: WorkflowExecutionId;
    workflow: T["workflow"];
    startedAt: string;
    state: WorkflowRunSequenceState;
    rows: WorkflowRunSequenceRow<T>[];
}

export const FOLLOWER_WINDOW_MS = 30_000;

interface Positioned<T> {
    execution: T;
    position: number | null;
    createdAt: number;
    finishedAt: number | null;
}

type FinishedEntry<T> = Positioned<T> & { finishedAt: number };

interface Draft<T> {
    root: T;
    members: Positioned<T>[];
    lastPosition: number;
}

interface FollowerLink<T> {
    predecessor: Positioned<T>;
    follower: Positioned<T>;
    distance: number;
}

const actionPosition = (execution: WorkflowRunSequenceExecution): number | null => {
    if (execution.actionId === null) {
        return null;
    }
    const index = execution.workflow.actions.findIndex(
        action => action.id === execution.actionId,
    );
    return index === -1 ? null : index;
};

const timestamp = (value: string): number => new Date(value).getTime();

const positioned = <T extends WorkflowRunSequenceExecution>(
    execution: T,
): Positioned<T> => ({
    execution,
    position: actionPosition(execution),
    createdAt: timestamp(execution.dateAdded),
    finishedAt: execution.finishedAt === null ? null : timestamp(execution.finishedAt),
});

const positionOrLast = (position: number | null): number =>
    position === null ? Number.MAX_SAFE_INTEGER : position;

const byDateThenPosition = <T extends WorkflowRunSequenceExecution>(
    a: Positioned<T>,
    b: Positioned<T>,
): number =>
    a.createdAt - b.createdAt || positionOrLast(a.position) - positionOrLast(b.position);

const byPositionThenDate = <T extends WorkflowRunSequenceExecution>(
    a: Positioned<T>,
    b: Positioned<T>,
): number =>
    positionOrLast(a.position) - positionOrLast(b.position) || a.createdAt - b.createdAt;

const byDistance = <T extends WorkflowRunSequenceExecution>(
    a: FollowerLink<T>,
    b: FollowerLink<T>,
): number => a.distance - b.distance || byDateThenPosition(a.follower, b.follower);

const scheduledAFollower = <T extends WorkflowRunSequenceExecution>(
    entry: Positioned<T>,
): entry is FinishedEntry<T> =>
    entry.finishedAt !== null &&
    (entry.execution.result === WorkflowExecutionResult.Success ||
        entry.execution.result === WorkflowExecutionResult.Warning);

const canBeAFollower = (entry: Positioned<WorkflowRunSequenceExecution>): boolean =>
    entry.execution.actionId !== null && entry.position !== 0;

const linkDistance = <T extends WorkflowRunSequenceExecution>(
    predecessor: FinishedEntry<T>,
    follower: Positioned<T>,
): number | null => {
    if (
        follower.execution.workflow.id !== predecessor.execution.workflow.id ||
        !canBeAFollower(follower)
    ) {
        return null;
    }
    if (follower.createdAt < predecessor.createdAt) {
        return null;
    }
    const distance = Math.abs(follower.createdAt - predecessor.finishedAt);
    return distance <= FOLLOWER_WINDOW_MS ? distance : null;
};

const followerLinks = <T extends WorkflowRunSequenceExecution>(
    entries: Positioned<T>[],
): FollowerLink<T>[] => {
    const links: FollowerLink<T>[] = [];
    for (const predecessor of entries) {
        if (!scheduledAFollower(predecessor)) {
            continue;
        }
        for (const follower of entries) {
            if (follower === predecessor) {
                continue;
            }
            const distance = linkDistance(predecessor, follower);
            if (distance !== null) {
                links.push({ predecessor, follower, distance });
            }
        }
    }
    return links.sort(byDistance);
};

const linkFollowers = <T extends WorkflowRunSequenceExecution>(
    entries: Positioned<T>[],
): Map<WorkflowExecutionId, Positioned<T>> => {
    const predecessorOf = new Map<WorkflowExecutionId, Positioned<T>>();
    const linked = new Set<WorkflowExecutionId>();
    for (const link of followerLinks(entries)) {
        if (
            linked.has(link.predecessor.execution.id) ||
            predecessorOf.has(link.follower.execution.id)
        ) {
            continue;
        }
        linked.add(link.predecessor.execution.id);
        predecessorOf.set(link.follower.execution.id, link.predecessor);
    }
    return predecessorOf;
};

const newDraft = <T extends WorkflowRunSequenceExecution>(
    entry: Positioned<T>,
): Draft<T> => ({
    root: entry.execution,
    members: [entry],
    lastPosition: entry.position ?? -1,
});

const continuesRun = <T extends WorkflowRunSequenceExecution>(
    current: Draft<T> | undefined,
    entry: Positioned<T>,
): current is Draft<T> =>
    current !== undefined &&
    entry.execution.actionId !== null &&
    (entry.position === null || entry.position > current.lastPosition);

const groupRoots = <T extends WorkflowRunSequenceExecution>(
    roots: Positioned<T>[],
    draftsByMember: Map<WorkflowExecutionId, Draft<T>>,
): Draft<T>[] => {
    const drafts: Draft<T>[] = [];
    const current = new Map<WorkflowId, Draft<T>>();
    for (const entry of roots) {
        const workflowId = entry.execution.workflow.id;
        const open = current.get(workflowId);
        if (continuesRun(open, entry)) {
            open.members.push(entry);
            open.lastPosition = Math.max(open.lastPosition, entry.position ?? -1);
            draftsByMember.set(entry.execution.id, open);
            continue;
        }
        const draft = newDraft(entry);
        drafts.push(draft);
        draftsByMember.set(entry.execution.id, draft);
        if (entry.execution.actionId !== null) {
            current.set(workflowId, draft);
        }
    }
    return drafts;
};

const attachFollowers = <T extends WorkflowRunSequenceExecution>(
    drafts: Draft<T>[],
    followers: Positioned<T>[],
    predecessorOf: Map<WorkflowExecutionId, Positioned<T>>,
    draftsByMember: Map<WorkflowExecutionId, Draft<T>>,
): void => {
    for (const entry of followers) {
        const predecessor = predecessorOf.get(entry.execution.id);
        const draft = predecessor && draftsByMember.get(predecessor.execution.id);
        if (draft) {
            draft.members.push(entry);
            draftsByMember.set(entry.execution.id, draft);
            continue;
        }
        const orphan = newDraft(entry);
        drafts.push(orphan);
        draftsByMember.set(entry.execution.id, orphan);
    }
};

const isPending = (execution: WorkflowRunSequenceExecution): boolean =>
    execution.finishedAt === null && execution.cancelledAt === null;

const reachedLastAction = (last: Positioned<WorkflowRunSequenceExecution>): boolean =>
    last.position === null
        ? last.execution.actionId === null
        : last.position === last.execution.workflow.actions.length - 1;

const sequenceState = <T extends WorkflowRunSequenceExecution>(
    members: Positioned<T>[],
): WorkflowRunSequenceState => {
    if (members.some(member => isPending(member.execution))) {
        return "inProgress";
    }
    const last = members[members.length - 1];
    if (last.execution.cancelledAt !== null) {
        return "cancelled";
    }
    if (
        last.execution.result === WorkflowExecutionResult.Error ||
        !reachedLastAction(last)
    ) {
        return "stopped";
    }
    return members.some(
        member => member.execution.result === WorkflowExecutionResult.Warning,
    )
        ? "completedWithWarnings"
        : "completed";
};

const highestPosition = <T extends WorkflowRunSequenceExecution>(
    members: Positioned<T>[],
): number => Math.max(-1, ...members.map(member => member.position ?? -1));

const earliest = <T extends WorkflowRunSequenceExecution>(
    members: Positioned<T>[],
): Positioned<T> =>
    members.reduce((found, member) =>
        member.createdAt < found.createdAt ? member : found,
    );

const remainingRows = <T extends WorkflowRunSequenceExecution>(
    members: Positioned<T>[],
    actionCount: number,
): WorkflowRunSequenceRow<T>[] => {
    const highest = highestPosition(members);
    const firstPosition = highest + 1;
    const lastPosition = actionCount - 1;
    if (highest === -1 || firstPosition > lastPosition) {
        return [];
    }
    return Array.from({ length: lastPosition - firstPosition + 1 }, (_, i) => ({
        kind: "notRun",
        key: `not-run-${firstPosition + i}`,
        position: firstPosition + i,
    }));
};

const finishSequence = <T extends WorkflowRunSequenceExecution>(
    draft: Draft<T>,
): WorkflowRunSequence<T> => {
    const members = [...draft.members].sort(byPositionThenDate);
    const state = sequenceState(members);
    return {
        id: draft.root.id,
        workflow: draft.root.workflow,
        startedAt: earliest(draft.members).execution.dateAdded,
        state,
        rows: [
            ...members.map(
                (member): WorkflowRunSequenceRow<T> => ({
                    kind: "executed",
                    key: member.execution.id,
                    position: member.position,
                    execution: member.execution,
                }),
            ),
            ...remainingRows(members, draft.root.workflow.actions.length),
        ],
    };
};

export const groupWorkflowRunSequences = <T extends WorkflowRunSequenceExecution>(
    executions: ReadonlyArray<T>,
): WorkflowRunSequence<T>[] => {
    const entries = executions.map(positioned).sort(byDateThenPosition);
    const predecessorOf = linkFollowers(entries);
    const draftsByMember = new Map<WorkflowExecutionId, Draft<T>>();
    const drafts = groupRoots(
        entries.filter(entry => !predecessorOf.has(entry.execution.id)),
        draftsByMember,
    );
    attachFollowers(
        drafts,
        entries.filter(entry => predecessorOf.has(entry.execution.id)),
        predecessorOf,
        draftsByMember,
    );
    return drafts
        .map(finishSequence)
        .sort((a, b) => timestamp(b.startedAt) - timestamp(a.startedAt));
};
